import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteDoc, doc, setDoc, updateDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { v4 } from 'uuid';
import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';
import { useToast } from '~/hooks/toast';

import { useProductsCompany } from '~/hooks/productsCompany';
import { bff } from '~/services/bff';
import { Status } from '~/components/Status';
import IComplement from '~/interfaces/IComplement';

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const Complements: React.FC = () => {
  const { addToast } = useToast();
  const {
    loadComplements,
    complements: groups,
    removeComplement,
    updateComplements,
  } = useProductsCompany();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Todos');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    if (!groups) loadComplements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadComplements]);

  const filteredItems = useMemo(() => {
    if (!groups) return [];

    const filtered = groups.filter(group => {
      let result = true;

      if (status === 'Ativo') {
        result =
          group.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) &&
          group.status === true;
      } else if (status === 'Inativo') {
        result =
          group.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) &&
          group.status === false;
      } else {
        result = group.name
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase());
      }

      return result;
    });

    return filtered;
  }, [search, groups, status]);

  const handleConfirmRemove = useCallback(
    async (id: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do grupo de complementos?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        await deleteDoc(doc(DB, 'complements', id));
        await bff.delete(`/complements/${REACT_APP_FIREBASE_PROJECT}`);

        removeComplement(id);

        // loadComplements();

        addToast({
          type: 'success',
          description: 'Grupo de complementos foi removido com sucesso.',
        });
      }
    },
    [addToast, removeComplement],
  );

  const handleDuplicateGroup = useCallback(
    async (groupId: string) => {
      try {
        if (groups) {
          const findGroup = groups.find(item => item.uid === groupId);

          if (findGroup) {
            const items = findGroup.items.map(item => ({
              ...item,
              uid: v4(),
            }));

            // console.log(items);

            const uid = v4();

            const parseData = {
              ...findGroup,
              uid,
              items,
            };

            await setDoc(doc(DB, 'complements', uid), parseData);

            updateComplements(parseData, true);

            // loadComplements();

            addToast({
              type: 'success',
              description: 'Grupo duplicado com sucesso!',
            });
          }
        }
      } catch (err) {
        console.log(err);
      }
    },
    [groups, addToast, updateComplements],
  );

  const handleChangeStatus = useCallback(
    async (item: IComplement) => {
      await updateDoc(doc(DB, 'complements', item.uid), {
        status: !item.status,
      });

      await bff.delete(`/complements/${REACT_APP_FIREBASE_PROJECT}`);

      updateComplements({ ...item, status: !item.status });
    },
    [updateComplements],
  );

  return (
    <Dashboard containerMessage="col-xl-10 offset-xl-1">
      <div className="col-xl-10 offset-xl-1">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h3 className="nk-block-title page-title">
                Grupos de Complementos
              </h3>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <em className="icon ni ni-search" />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          id="default-04"
                          placeholder="Buscar por grupo de complemento"
                          onChange={event => {
                            setSearch(event.target.value);
                          }}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="drodown">
                        <Link
                          to="#status"
                          className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                          data-toggle="dropdown"
                        >
                          {status}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <Link
                                to="#status"
                                onClick={() => setStatus('Todos')}
                              >
                                <span>Todos</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#active"
                                onClick={() => setStatus('Ativo')}
                              >
                                <span>Ativo</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#inactive"
                                onClick={() => setStatus('Inativo')}
                              >
                                <span>Inativo</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li className="nk-block-tools-opt">
                      <Link
                        to="/complements/create"
                        data-target="addProduct"
                        className="toggle btn btn-icon btn-primary d-md-none"
                      >
                        <em className="icon ni ni-plus" />
                      </Link>
                      <Link
                        to="/complements/create"
                        data-target="addProduct"
                        className="toggle btn btn-primary d-none d-md-inline-flex"
                      >
                        <em className="icon ni ni-plus" />
                        <span>Criar Grupo</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="nk-block">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <div className="nk-tb-list">
                  <div className="nk-tb-item nk-tb-head">
                    <div className="nk-tb-col">
                      <span>Ordem</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Nome</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Nome de Identificação</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Tipo</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Status</span>
                    </div>

                    <div className="nk-tb-col nk-tb-col-tools">
                      <ul className="nk-tb-actions gx-1 my-n1">
                        <li className="mr-n1" />
                      </ul>
                    </div>
                  </div>

                  {filteredItems.map(item => (
                    <div className="nk-tb-item" key={item.uid}>
                      <div className="nk-tb-col">
                        <span className="title">{item.order}</span>
                      </div>

                      <div className="nk-tb-col">
                        <span className="title">{item.name}</span>
                      </div>

                      <div className="nk-tb-col">
                        <span className="title">{item.name2}</span>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span className="fs-14px">
                            {item.type === '0' ? 'Opcional' : 'Obrigatório'}
                          </span>
                        </div>
                      </div>
                      <div className="nk-tb-col">
                        <Status
                          value={item.status}
                          onChange={() => handleChangeStatus(item)}
                        />
                      </div>
                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1">
                            <div className="dropdown">
                              <Link
                                to="#options"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                                data-toggle="dropdown"
                              >
                                <em className="icon ni ni-more-h" />
                              </Link>
                              <div className="dropdown-menu dropdown-menu-right">
                                <ul className="link-list-opt no-bdr">
                                  <li>
                                    <Link
                                      to={{
                                        pathname: `/complements/items/${item.uid}`,
                                        state: {
                                          item,
                                        },
                                      }}
                                    >
                                      <em className="icon ni ni-plus" />
                                      <span>Incluir Itens</span>
                                    </Link>
                                  </li>
                                  <li>
                                    <Link
                                      to="#duplicate"
                                      onClick={() => {
                                        handleDuplicateGroup(item.uid);
                                      }}
                                    >
                                      <em className="icon ni ni-plus" />
                                      <span>Duplicar grupo</span>
                                    </Link>
                                  </li>
                                  <li>
                                    <Link
                                      to={{
                                        pathname: `/complements/edit/${item.uid}`,
                                        state: {
                                          item,
                                        },
                                      }}
                                    >
                                      <em className="icon ni ni-edit" />
                                      <span>Editar</span>
                                    </Link>
                                  </li>

                                  <li>
                                    <Link
                                      to="#remove"
                                      onClick={() => {
                                        handleConfirmRemove(item.uid);
                                      }}
                                    >
                                      <em className="icon ni ni-trash" />
                                      <span>Excluir</span>
                                    </Link>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default Complements;
